import { Link } from "react-router-dom";
import {
  FaSpider,
  FaRobot,
  FaChartBar,
  FaServer,
  FaDatabase,
  FaPlug,
  FaCode,
} from "react-icons/fa";
import Process from "./process";

const groups = [
  {
    id: "collect",
    label: "Collect",
    title: "Get the data",
    blurb:
      "Scrapers and crawlers that pull structured data from sites with no API, including JavaScript-heavy pages.",
    services: [
      {
        icon: FaSpider,
        title: "Web Scraping Services",
        featured: true,
        description:
          "Professional web scraping services in Nepal: automated data extraction from eCommerce platforms, business directories, and any website at scale.",
        points: [
          "Handles JavaScript-rendered pages",
          "Output as JSON, CSV or through an API",
          "Scheduled, repeatable runs",
        ],
        tags: ["Scrapy", "Playwright", "Puppeteer"],
      },
      {
        icon: FaRobot,
        title: "SERP & Competitor Scraping",
        description:
          "Track search engine rankings, monitor competitor pricing, and extract keyword data automatically.",
        points: [
          "Keyword rank tracking over time",
          "Competitor price and listing monitoring",
          "Proxy rotation to avoid blocks",
        ],
        tags: ["Playwright", "Proxy rotation", "Cron"],
      },
      {
        icon: FaCode,
        title: "Scraping Bot & Automation",
        description:
          "Headless browser automation for JavaScript-rendered pages, anti-bot measures and scheduled crawlers.",
        points: [
          "Puppeteer and Playwright browsers",
          "Anti-bot and proxy handling",
          "Crawlers that run on a schedule",
        ],
        tags: ["Puppeteer", "Playwright", "Headless"],
      },
    ],
  },
  {
    id: "process",
    label: "Store",
    title: "Clean and store it",
    blurb:
      "Raw pages become clean, structured records in a database you can query.",
    services: [
      {
        icon: FaChartBar,
        title: "Data Extraction & ETL",
        description:
          "End-to-end pipelines that collect raw web data and deliver it clean and structured for your business.",
        points: [
          "Extract, clean and structure raw data",
          "Load into SQL or NoSQL databases",
          "Delivery as files or through an API",
        ],
        tags: ["Python", "Node.js", "PostgreSQL"],
      },
      {
        icon: FaDatabase,
        title: "Database Design & Storage",
        description:
          "Designing and optimizing PostgreSQL, MongoDB and MySQL databases to store, query, and manage large volumes of scraped data.",
        points: [
          "Schema design for large datasets",
          "PostgreSQL, MongoDB and MySQL",
          "Query and storage optimization",
        ],
        tags: ["PostgreSQL", "MongoDB", "MySQL"],
      },
    ],
  },
  {
    id: "build",
    label: "Build",
    title: "Put it to work",
    blurb:
      "The backend that schedules scrapers, stores results and exposes the data to your tools.",
    services: [
      {
        icon: FaServer,
        title: "Back-End Development",
        description:
          "Robust server-side systems built with Node.js, Python and Express, handling authentication, data storage, scheduling, and scraper orchestration.",
        points: [
          "Node.js and Python services",
          "Authentication and data storage",
          "Scheduling and scraper orchestration",
        ],
        tags: ["Node.js", "Python", "Express"],
      },
      {
        icon: FaPlug,
        title: "API Development",
        description:
          "Custom REST APIs that expose your scraped datasets, trigger collection and integrate with your existing tools and workflows.",
        points: [
          "REST APIs for your scraped data",
          "Trigger collection through endpoints",
          "Integrates with your existing tools",
        ],
        tags: ["REST", "Express", "Node.js"],
      },
    ],
  },
];

const Check = () => (
  <svg
    className="mt-1 h-4 w-4 shrink-0 text-primary-400"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="m5 13 4 4L19 7" />
  </svg>
);

const ServiceCard = ({ service }) => {
  const Icon = service.icon;
  return (
    <article
      className={`glass-card group relative flex flex-col p-6 hover:border-primary-500/50 ${
        service.featured ? "sm:col-span-2 sm:p-8" : ""
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <span className="flex h-12 w-12 items-center justify-center rounded-lg border border-primary-500/20 bg-primary-500/10 text-primary-400">
          <Icon className="h-5 w-5" aria-hidden="true" />
        </span>
        {service.featured && (
          <span className="font-code text-xs uppercase tracking-wider text-amber-400">
            Core service
          </span>
        )}
      </div>

      <h3 className="mt-5 text-xl font-semibold">{service.title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-gray-400">
        {service.description}
      </p>

      <ul
        className={`mt-5 space-y-2 text-sm text-gray-300 ${
          service.featured ? "sm:grid sm:grid-cols-3 sm:gap-x-6 sm:space-y-0" : ""
        }`}
      >
        {service.points.map((point) => (
          <li key={point} className="flex gap-2">
            <Check />
            <span>{point}</span>
          </li>
        ))}
      </ul>

      <div className="mt-6 flex flex-wrap gap-2 border-t border-dark-600 pt-5">
        {service.tags.map((tag) => (
          <span
            key={tag}
            className="rounded-md border border-dark-600 bg-dark-700 px-2.5 py-1 font-code text-xs text-gray-300"
          >
            {tag}
          </span>
        ))}
      </div>
    </article>
  );
};

const Services = ({ standalone = false }) => {
  const HeadingTag = standalone ? "h1" : "h2";
  return (
    <>
      <section className="border-t border-dark-600 bg-dark-900/40 py-20 text-light-100 lg:py-24">
        <div className="container mx-auto px-6">
          <header className="mb-16 max-w-3xl">
            <p className="mb-3 font-code text-sm text-primary-400">Services</p>
            <HeadingTag className="mb-5 text-3xl font-bold md:text-5xl md:leading-tight">
              {standalone
                ? "Backend Development & Web Scraping Services in Nepal"
                : "What I build"}
            </HeadingTag>
            <p className="text-lg text-gray-300">
              Web scraping services in Nepal, from automated data extraction to
              the backend systems that store, schedule and serve the data.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-6">
              <Link
                to="/contact"
                className="rounded-md bg-primary-500 px-6 py-3 font-semibold text-dark-900 transition-colors hover:bg-primary-400"
              >
                Get in touch
              </Link>
              <Link
                to="/portfolio"
                className="underline decoration-dark-500 underline-offset-4 transition-colors hover:decoration-primary-400"
              >
                See my work
              </Link>
            </div>
          </header>

          <div className="space-y-16">
            {groups.map((group, i) => (
              <div
                key={group.id}
                className="grid gap-8 lg:grid-cols-12 lg:gap-12"
              >
                <div className="lg:col-span-4">
                  <div className="lg:sticky lg:top-28">
                    <span className="font-code text-sm text-amber-400">
                      {String(i + 1).padStart(2, "0")} / {group.label}
                    </span>
                    <h2 className="mt-3 text-2xl font-bold">{group.title}</h2>
                    <p className="mt-3 text-gray-400">{group.blurb}</p>
                  </div>
                </div>

                <div className="grid gap-5 sm:grid-cols-2 lg:col-span-8">
                  {group.services.map((service) => (
                    <ServiceCard key={service.title} service={service} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {standalone && <Process />}

      {standalone && (
        <section className="border-t border-dark-600 bg-dark-800/60 py-16 text-light-100">
          <div className="container mx-auto flex flex-col gap-6 px-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="max-w-xl">
              <h2 className="text-2xl font-bold">
                Need a custom scraping solution?
              </h2>
              <p className="mt-2 text-gray-400">
                Price monitoring, SERP tracking, lead generation or a custom
                extraction pipeline. Tell me what data you need and how often.
              </p>
            </div>
            <Link
              to="/contact"
              className="shrink-0 rounded-md bg-primary-500 px-6 py-3 text-center font-semibold text-dark-900 transition-colors hover:bg-primary-400"
            >
              Start a project
            </Link>
          </div>
        </section>
      )}
    </>
  );
};

export default Services;
